import $ from 'jquery';

import Section from '../Section';
import KenBurns from '../global/KenBurns';
import Parallax from '../global/Parallax';

export default class MapSection extends Section {
  /**
   * @param {import('../ThemeBase').default} theme
   * @param {HTMLElement} element
   * @param {boolean} pageLoad
   */
  constructor(theme, element, pageLoad) {
    super(theme, element, pageLoad);
    this.mapsApi = null;

    this.mapElement = /** @type {HTMLElement} */ (element.querySelector('.map-wrapper'));
    this.contentBox = /** @type {HTMLElement} */ (element.querySelector('.homepage-map-content-box'));
    
    if(this.contentBox) {
      this.contentBoxAllignment = this.element.dataset.mapsAlignment === 'right' ? 'left' : 'right';
      this.contentBoxMargin = parseInt(window.getComputedStyle(this.contentBox).getPropertyValue(`margin-${this.contentBoxAllignment}`));
    }

    this.apiKey = this.element.dataset.mapsApikey;
    this.address = this.element.dataset.mapsAddress;

    if(!this.apiKey || !this.address) return;

    theme.loadGoogleMapApi(this.apiKey).then(this.onLoad).catch(this.showError);
    document.body.addEventListener('google-maps-auth-failure', this.onAuthError);

    this.kenBurns = new KenBurns(theme, element);
    this.parallax = new Parallax(theme, element);
  }

  onAuthError = (e) => {
    this.showError(e.detail.reason);
  }

  showError = (message) => {
    const container = this.element.querySelector('.map-section__container');
    container.classList.add('hide');
    if (window.Shopify.designMode) {
      $('<div class="map-container-error"></div>').text(message).appendTo(container.parentElement.querySelector('.map-section__overlay'));
    }
    container.parentElement.querySelector('.homepage-map--fallback').classList.remove('hide');
  }

  /**
   * This is called when Maps API is loaded
   * @param {import('../window').GoogleMapsApi} maps
   */
  onLoad = (maps) => {
    this.mapsApi = maps;
    const config = {
      zoom: 14
    };

    let style = [];
    try {
      const mapThemeElem = this.element.querySelector('.map-theme');
      if(mapThemeElem) {
        style = JSON.parse(mapThemeElem.textContent);
      }
    } catch (e) {
      console.error('Unable to parse theme style:', e);
    }
    const container = /** @type {HTMLElement} */ (this.element.querySelector('.map-section__container'));
    if (!container) {
      return;
    }

    this.geocoder = new maps.Geocoder();

    this.geocoder.geocode({'address': this.address}, (results, status) => {
      if (status === 'OK') {

        /**
         * @typedef {import('../window').GoogleMap} GoogleMap
         *
         */
        this.map = /** @type {GoogleMap} */ (new maps.Map(container, {
          zoom: config.zoom,
          styles: style,
          disableDefaultUI: true,
          draggable: false,
          clickableIcons: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          draggableCursor: 'default'
        }));

        this.mapsApi.event.addListener(this.map, "projection_changed", () => {
          this.centerMap();
          if(this.contentBox) {
            // Center map when content box is resized
            // works as windiw resize observer as well since the content box has relative width

            // @ts-ignore
            new ResizeObserver(this.centerMap).observe(this.contentBox);
          } else {
            // Center map when screensize is changed
            // redundant if content box resize observer is active
            window.addEventListener('resize', this.centerMap);
          }
        });
        
        /**
         *
         * @typedef {import('../window').GoogleMapsMarker} GoogleMapsMarkerType
         */
        this.marker = /** @type {GoogleMapsMarkerType} */ (new maps.Marker({
          map: this.map,
          position: results[0].geometry.location
        }));
      } else {
        this.showError('Error looking up that address');
      }
    });
  }

  offsetCenter = (offsetX, offsetY) => {
    // offsetX is the distance you want that point to move to the right, in pixels
    // offsetY is the distance you want that point to move upwards, in pixels
    // offset values can be negative

    const markerPosition = this.marker.getPosition();

    const scale = Math.pow(2, this.map.getZoom());

    const mapProjection = this.map.getProjection();
    const worldCoordinateCenter = mapProjection.fromLatLngToPoint(markerPosition);
    const pixelOffset = new this.mapsApi.Point((offsetX/scale) || 0,(offsetY/scale) || 0);

    const worldCoordinateNewCenter = new this.mapsApi.Point(
      worldCoordinateCenter.x - pixelOffset.x,
      worldCoordinateCenter.y + pixelOffset.y
    );

    const newCenter = mapProjection.fromPointToLatLng(worldCoordinateNewCenter);

    this.map.setCenter(newCenter);
  }

  centerMap = () => {
    if (!this.map) return;

    if(this.contentBox) {
      const totalContentBoxWidth = this.contentBox.offsetWidth + this.contentBoxMargin;
      const totalContentBoxHeight = this.contentBox.offsetHeight + this.contentBoxMargin;
      if(window.innerWidth > 767) {
        if(this.contentBoxAllignment === 'left') {
          this.offsetCenter(totalContentBoxWidth / 2, 0);
        } else {
          this.offsetCenter(-(totalContentBoxWidth / 2), 0);
        }
      } else {
        this.offsetCenter(0, -(totalContentBoxHeight / 2));
      }
    } else {
      this.map.setCenter(this.marker.getPosition());
    }
  }
}
